import React, { useEffect } from 'react';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import Title from 'ui/Typography/Title';
import styled from 'styled-components';
import Body from 'ui/Typography/Body';
import ekrisLogo from './ekris-logo.jpg';
import { extractImageSource } from 'utils/imageUtils';
import Button from 'ui/Button/Button';
import ShapeFreeIcon from '@ingka/ssr-icon/paths/shape-free';
import StopIcon from '@ingka/ssr-icon/paths/stop';
import { useAppDispatch } from 'ducks/hooks';
import { EkrisFeature } from 'rangeConfig/eket/features/Ekris/EkrisFeature';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { endGame, setGameOver } from './ekrisReducer';
import useRestartPlanner from 'hooks/useRestartPlanner';
import EkrisGameOverModal from './EkrisGameOverModal';
import { hideMeasurements } from 'ducks/layout';
import { getMarketProducts } from 'ducks/products';
import { useSelector } from 'react-redux';
import { ProductType } from '@inter-ikea-storageone/range';

const Wrapper = styled.div`
  display: flex;
  position: sticky;
  flex-direction: column;
  top: 0;
  background: white;
  padding-top: ${({ theme }) => theme.space.l};
  padding-bottom: 0;
  z-index: 200;
  border: 0;
  margin: ${({ theme }) => theme.space.l} ${({ theme }) => theme.space.xxl};
`;
const StyledImage = styled.img`
  max-width: 100%;
  height: auto;
  display: block;
  padding-bottom: ${({ theme }) => theme.space.xl};
`;
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const EkrisMenu: React.FunctionComponent<object> = () => {
  const ekrisImage = [{ src: ekrisLogo, width: 328, height: 384 }];
  const { fallbackImage } = extractImageSource(ekrisImage);
  const marketProducts = useSelector(getMarketProducts);
  const dispatch = useAppDispatch();
  const restartPlanner = useRestartPlanner();
  const {
    plannerConfig: { features }
  } = usePlannerConfig();
  const ekrisFeature = features.find(feature => feature.id === 'Ekris')?.feature;
  const eketFrames = marketProducts.filter(product => product.type === ProductType.EKET_FRAME);

  useEffect(() => {
    dispatch(hideMeasurements());
    (ekrisFeature as EkrisFeature)?.getGame()?.startGame(eketFrames, handleGameOver);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRestart = () => {
    (ekrisFeature as EkrisFeature)?.getGame()?.restartGame(eketFrames, handleGameOver);
    dispatch(setGameOver(false));
    // Focus on canvas to enable keyboard controls after restarting the game
    const canvas: HTMLCanvasElement = (document.querySelector(
      '[data-testid="canvas"]'
    ) as unknown) as HTMLCanvasElement;

    if (canvas) {
      canvas.focus();
    }
  };

  const handleEndGame = () => {
    dispatch(endGame());
    dispatch(setGameOver(false));
    (ekrisFeature as EkrisFeature)?.getGame()?.stopGame();
    restartPlanner();
  };

  const handleGameOver = () => {
    dispatch(setGameOver(true));
  };

  return (
    <Wrapper>
      <EkrisGameOverModal />
      <StyledImage src={fallbackImage.src} alt={'Ekris'} />
      <Body type={ThemeFontStyleTypeEnum.bodyLarge}>
        Challenge yourself or a friend too see who can get the highest combined price and get onto
        the highscore board!
      </Body>
      <Title
        display="flex"
        alignItems="center"
        type={ThemeFontStyleTypeEnum.headingMedium}
        mt="md"
        mb="xs"
      >
        Gameplay
      </Title>
      <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
        The goal of Ekris is to get the highest combined price by clearing horizontal lines of EKET
        frames. You must rotate, move and drop the falling EKET frames in the scene. A row of EKET
        frames will be cleared once a row is filled with frames and has no empty spaces.
      </Body>
      <Title
        display="flex"
        alignItems="center"
        type={ThemeFontStyleTypeEnum.headingMedium}
        mt="md"
        mb="xs"
      >
        Controls
      </Title>
      <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
        <b>Arrow left</b> - Move frame to the left
        <br />
        <b>Arrow right</b> - Move frame to the right
        <br />
        <b>Arrow up</b> - Rotate frame
        <br />
        <b>Arrow down</b> - Soft drop down
        <br />
        <b>Space</b> - Hard drop down
      </Body>
      <ButtonWrapper>
        <Button type="primary" onClick={handleRestart} size={'medium'} ssrIcon={ShapeFreeIcon}>
          Start over
        </Button>
        <Button type="secondary" onClick={handleEndGame} size={'medium'} ssrIcon={StopIcon}>
          Back to planner
        </Button>
      </ButtonWrapper>
    </Wrapper>
  );
};
export default EkrisMenu;
